/**
 * BlogSearchFloating - floating/sticky variant of the blog search input.
 * Custom element: <blog-search-floating>
 *
 * Refs:
 *   [data-ref="triggerButton"]  (required)
 *   [data-ref="searchForm"]  (required)
 *   [data-ref="searchInput"]  (required)
 *   [data-ref="overlayContent"]  (required)
 *   [data-ref="closeButton"]  (required)
 *
 * Loading: lazy-loaded by the theme loader (theme.js), which imports this
 * file by its exact name - the filename is load-bearing (import map in
 * snippets/theme-head.liquid).
 * Reconstructed to readable identifiers from the original minified vendor
 * bundle; behaviour verified unchanged by AST alpha-equivalence.
 */
var defineProperty = Object.defineProperty;
var setFunctionName = (target, functionName) => defineProperty(target, "name", {
  value: functionName,
  configurable: !0
});
import "./component-BodyOverlay.js";
import { ThemeComponent, Blog } from "./theme.js";
import "./vendor.js";
class BlogSearchFloating extends ThemeComponent {
  constructor() {
    super(...arguments), this.globalOverlay = null, this.lastScrollTop = 0, this.scrollThreshold = 200;
  }
  init() {
    this.$log.info("BlogSearchFloating: init() called");
    const overlayElement = document.querySelector('[data-ref="bodyOverlay"]');
    if (overlayElement) this.globalOverlay = overlayElement, this.$log.info("BlogSearchFloating: Global BodyOverlay found");else {
      this.$log.error("BlogSearchFloating: Global BodyOverlay not found");
      return;
    }
    const trigger = this.triggerButton || this.querySelector('[data-ref="triggerButton"]');
    if (!trigger) {
      this.$log.error("BlogSearchFloating: triggerButton not found via ref or querySelector");
      return;
    }
    this.$log.info("BlogSearchFloating: triggerButton found", trigger);
    const form = this.searchForm || this.querySelector('[data-ref="searchForm"]'),
      input = this.searchInput || this.querySelector('[data-ref="searchInput"]');
    if (!form) {
      this.$log.error("BlogSearchFloating: searchForm not found");
      return;
    }
    if (this.$log.info("BlogSearchFloating: searchForm found", form), !input) {
      this.$log.error("BlogSearchFloating: searchInput not found");
      return;
    }
    this.$log.info("BlogSearchFloating: searchInput found", input);
    const content = this.overlayContent || this.querySelector('[data-ref="overlayContent"]');
    if (!content) {
      this.$log.error("BlogSearchFloating: overlayContent not found");
      return;
    }
    this.$log.info("BlogSearchFloating: overlayContent found", content);
    const closeBtn = this.closeButton || this.querySelector('[data-ref="closeButton"]');
    if (!closeBtn) {
      this.$log.error("BlogSearchFloating: closeButton not found");
      return;
    }
    this.$log.info("BlogSearchFloating: closeButton found", closeBtn), this.triggerButton || (this.triggerButton = trigger), this.searchForm || (this.searchForm = form), this.searchInput || (this.searchInput = input), this.overlayContent || (this.overlayContent = content), this.closeButton || (this.closeButton = closeBtn), this.$log.info("BlogSearchFloating: Attaching click listener to triggerButton"), trigger.addEventListener("click", triggerClickEvent => {
      triggerClickEvent.preventDefault(), triggerClickEvent.stopPropagation(), this.$log.info("BlogSearchFloating: Button clicked, opening overlay"), this.openOverlay();
    }), this.$log.info("BlogSearchFloating: Click listener attached"), this.globalOverlay && this.$subscribe(this.globalOverlay.onCloseChange, () => {
      this.closeOverlay();
    }), content.addEventListener("click", contentClickEvent => {
      contentClickEvent.stopPropagation();
    }), closeBtn.addEventListener("click", closeClickEvent => {
      closeClickEvent.preventDefault(), closeClickEvent.stopPropagation(), this.$log.info("BlogSearchFloating: Close button clicked"), this.closeOverlay();
    }), form.addEventListener("submit", async submitEvent => {
      submitEvent.preventDefault(), submitEvent.stopPropagation();
      const searchTerm = input.value.trim();
      return searchTerm ? (this.closeOverlay(), await this.performSearch(searchTerm)) : (this.closeOverlay(), await this.clearSearch()), !1;
    }), this.$listen(window, "keydown", keydownEvent => {
      keydownEvent.key === "Escape" && this.globalOverlay && !this.globalOverlay.classList.contains("visibility-hidden") && this.closeOverlay();
    }), this.setupScrollHandler(), this.hideTriggerButton();
  }
  setupScrollHandler() {
    this.$listen(window, "scroll", () => {
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      Math.abs(scrollPosition - this.lastScrollTop) < 10 || this.globalOverlay && !this.globalOverlay.classList.contains("visibility-hidden") || (scrollPosition > this.scrollThreshold ? this.showTriggerButton() : this.hideTriggerButton(), this.lastScrollTop = scrollPosition);
    });
  }
  showTriggerButton() {
    this.triggerButton && (this.triggerButton.style.visibility = "visible", this.triggerButton.style.opacity = "1", this.triggerButton.style.pointerEvents = "auto");
  }
  hideTriggerButton() {
    this.triggerButton && (this.triggerButton.style.visibility = "hidden", this.triggerButton.style.opacity = "0", this.triggerButton.style.pointerEvents = "none");
  }
  async performSearch(searchQuery) {
    const blogMain = document.querySelector('[data-ref="blogMain"]');
    if (!blogMain) {
      this.$log.error("BlogMain component not found");
      return;
    }
    const blogHandle = blogMain.getAttribute("blog-handle") || "blog",
      isoCode = blogMain.getAttribute("isocode") || "en";
    try {
      const renderedSections = await Blog.render(["blog-main"], blogHandle, isoCode, searchQuery),
        parsedContainer = document.createElement("div");
      parsedContainer.innerHTML = renderedSections["blog-main"];
      const newArticlesContainer = parsedContainer.querySelector('[data-ref="articlesContainer"]'),
        existingArticlesContainer = blogMain.querySelector('[data-ref="articlesContainer"]');
      newArticlesContainer && existingArticlesContainer && (existingArticlesContainer.outerHTML = newArticlesContainer.outerHTML);
      const newPaginationBar = parsedContainer.querySelector('[data-ref="paginationBar"]'),
        existingPaginationBar = blogMain.querySelector('[data-ref="paginationBar"]');
      newPaginationBar && existingPaginationBar ? existingPaginationBar.outerHTML = newPaginationBar.outerHTML : newPaginationBar && !existingPaginationBar ? blogMain.appendChild(newPaginationBar) : !newPaginationBar && existingPaginationBar && existingPaginationBar.remove();
      const searchUrl = new URL(window.location.href);
      searchUrl.searchParams.set("q", searchQuery), searchUrl.searchParams.has("page") && searchUrl.searchParams.delete("page"), window.history.pushState({}, "", searchUrl.toString()), blogMain.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    } catch (searchError) {
      this.$log.error("Error performing search:", searchError);
    }
  }
  openOverlay() {
    this.$log.info("BlogSearchFloating: openOverlay called"), this.hideTriggerButton();
    const topBar = document.querySelector(".js-top-bar");
    topBar && (topBar.style.display = "none", this.$log.info("BlogSearchFloating: Hidden top-bar")), this.globalOverlay && (this.globalOverlay.openOverlay(), this.$log.info("BlogSearchFloating: Opened global BodyOverlay")), this.overlayContent && (this.overlayContent.classList.remove("visibility-hidden"), this.overlayContent.style.visibility = "visible", this.overlayContent.style.opacity = "1", this.overlayContent.style.pointerEvents = "auto", this.$log.info("BlogSearchFloating: Made overlay content visible")), setTimeout(() => {
      this.searchInput && this.searchInput.focus();
    }, 100);
  }
  closeOverlay() {
    this.$log.info("BlogSearchFloating: closeOverlay called"), (window.pageYOffset || document.documentElement.scrollTop) > this.scrollThreshold ? this.showTriggerButton() : this.hideTriggerButton();
    const topBarForClose = document.querySelector(".js-top-bar");
    topBarForClose && (topBarForClose.style.display = "", this.$log.info("BlogSearchFloating: Shown top-bar")), this.globalOverlay && (this.globalOverlay.closeOverlay(), this.$log.info("BlogSearchFloating: Closed global BodyOverlay")), this.overlayContent && (this.overlayContent.classList.add("visibility-hidden"), this.overlayContent.style.visibility = "hidden", this.overlayContent.style.opacity = "0", this.overlayContent.style.pointerEvents = "none"), this.searchInput && (this.searchInput.value = "");
  }
  async clearSearch() {
    const blogMain = document.querySelector('[data-ref="blogMain"]');
    if (!blogMain) {
      this.$log.error("BlogMain component not found");
      return;
    }
    const blogHandle = blogMain.getAttribute("blog-handle") || "blog",
      isoCode = blogMain.getAttribute("isocode") || "en";
    try {
      const renderedSections = await Blog.render(["blog-main"], blogHandle, isoCode),
        parsedContainer = document.createElement("div");
      parsedContainer.innerHTML = renderedSections["blog-main"];
      const newArticlesContainer = parsedContainer.querySelector('[data-ref="articlesContainer"]'),
        existingArticlesContainer = blogMain.querySelector('[data-ref="articlesContainer"]');
      newArticlesContainer && existingArticlesContainer && (existingArticlesContainer.outerHTML = newArticlesContainer.outerHTML);
      const newPaginationBar = parsedContainer.querySelector('[data-ref="paginationBar"]'),
        existingPaginationBar = blogMain.querySelector('[data-ref="paginationBar"]');
      newPaginationBar && existingPaginationBar ? existingPaginationBar.outerHTML = newPaginationBar.outerHTML : newPaginationBar && !existingPaginationBar ? blogMain.appendChild(newPaginationBar) : !newPaginationBar && existingPaginationBar && existingPaginationBar.remove();
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("q"), cleanUrl.searchParams.delete("page"), window.history.pushState({}, "", cleanUrl.toString()), blogMain.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    } catch (clearError) {
      this.$log.error("Error clearing search:", clearError);
    }
  }
}
setFunctionName(BlogSearchFloating, "BlogSearchFloating");
const propertyTypes = {};
BlogSearchFloating.prototype.$name = "BlogSearchFloating";
BlogSearchFloating.prototype.$info = JSON.parse('{"name":"BlogSearchFloating","meta":{"isLegacy":false,"selector":"blog-search-floating"},"props":[],"refs":[{"selector":"[data-ref=\\"triggerButton\\"]","required":true,"propertyKey":"triggerButton","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLButtonElement"},{"selector":"[data-ref=\\"searchForm\\"]","required":true,"propertyKey":"searchForm","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLFormElement"},{"selector":"[data-ref=\\"searchInput\\"]","required":true,"propertyKey":"searchInput","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLInputElement"},{"selector":"[data-ref=\\"overlayContent\\"]","required":true,"propertyKey":"overlayContent","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLElement"},{"selector":"[data-ref=\\"closeButton\\"]","required":true,"propertyKey":"closeButton","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLButtonElement"}]}');
BlogSearchFloating.prototype.$info.component = BlogSearchFloating;
for (const prop of BlogSearchFloating.prototype.$info.props) prop.propertyType = propertyTypes[prop.propertyType];
window.componentService.registerComponent("Blog/BlogSearchFloating.ts", {
  BlogSearchFloating: BlogSearchFloating
}, void 0);